import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Lock, ShieldCheck, ArrowRight } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { formatAUD } from "@/lib/media";
import { useSeo } from "@/lib/seo";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PROMO_STORAGE_KEY } from "@/components/SeasonalBanner";
import CheckoutPostcode, { windowLabel } from "@/components/CheckoutPostcode";
import { trackInitiateCheckout, trackPlaceAnOrder, identify } from "@/lib/pixel";

export default function Checkout() {
  useSeo("Checkout", "Secure checkout for your Chewie & Solo goodies.");
  const { cart, subtotal } = useStore();
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [loading, setLoading] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [postcode, setPostcode] = useState(() => localStorage.getItem("hb_postcode") || "");
  const [estimate, setEstimate] = useState(null);
  const [pickup, setPickup] = useState(false);
  const pickupPossible = cart.length > 0 && cart.every((i) => i.type === "bundle" ? false : (i.local_pickup || i.printify)) && cart.some((i) => i.local_pickup) && !cart.some((i) => i.printify);
  useEffect(() => { api.get("/payments/config").then(({ data }) => setTestMode(data.mode === "test")).catch(() => {}); }, []);
  const [giftCode, setGiftCode] = useState("");
  const [applied, setApplied] = useState(null);
  const [checking, setChecking] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promo, setPromo] = useState(null);
  const [checkingPromo, setCheckingPromo] = useState(false);

  const applyPromo = async (codeArg) => {
    const code = (typeof codeArg === "string" ? codeArg : promoCode).trim();
    if (!code) return;
    setCheckingPromo(true);
    try {
      const items = cart.filter((i) => i.type !== "bundle").map((i) => ({ slug: i.slug, price: i.price, quantity: i.quantity }));
      const { data } = await api.post("/promo-codes/validate", { code, subtotal, items });
      if (data.valid) { setPromoCode(code.toUpperCase()); setPromo({ code: code.toUpperCase(), discount: data.discount, kind: data.kind, value: data.value }); toast.success("Promo code applied! 🎉"); }
      else { setPromo(null); toast.error(data.message || "That code isn't valid."); }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCheckingPromo(false); }
  };

  // A code grabbed from the seasonal banner applies itself once the cart has items.
  useEffect(() => {
    const saved = localStorage.getItem(PROMO_STORAGE_KEY);
    if (saved && !promo && subtotal > 0) { localStorage.removeItem(PROMO_STORAGE_KEY); applyPromo(saved); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  const applyGift = async () => {
    if (!giftCode.trim()) return;
    setChecking(true);
    try {
      const { data } = await api.post("/gift-cards/validate", { code: giftCode.trim() });
      if (data.valid) { setApplied({ code: giftCode.trim().toUpperCase(), balance: data.balance }); toast.success(`Gift card applied — ${formatAUD(data.balance)} available 🐾`); }
      else { setApplied(null); toast.error(data.message || "That code isn't valid."); }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setChecking(false); }
  };

  const promoDiscount = promo ? Math.min(promo.discount, subtotal) : 0;
  const giftDiscount = applied ? Math.min(applied.balance, Math.max(0, subtotal - promoDiscount)) : 0;
  const estimatedTotal = Math.max(0, subtotal - promoDiscount - giftDiscount);

  const pay = async (e) => {
    e.preventDefault();
    if (cart.length === 0) { toast.error("Your bag is empty."); return; }
    setLoading(true);
    trackInitiateCheckout(cart, estimatedTotal);
    try {
      const regular = cart.filter((i) => i.type !== "bundle");
      const bundleItems = cart.filter((i) => i.type === "bundle");
      const items = regular.map((i) => ({ product_id: i.id, quantity: i.quantity, size: i.size, colour: i.colour, personalisation: i.personalisation || null }));
      const bundles = bundleItems.map((b) => ({
        quantity: b.quantity, kind: b.kind || "default",
        components: b.components.map((c) => ({ product_id: c.product_id, size: c.size, colour: c.colour })),
      }));
      const { data } = await api.post("/checkout", {
        items, bundles, origin_url: window.location.origin, email,
        user_id: user ? user.id : null,
        gift_card_code: applied ? applied.code : null,
        promo_code: promo ? promo.code : null,
        postcode: /^\d{4}$/.test(postcode) ? postcode : null,
        pickup: pickupPossible && pickup,
      });
      await identify({ email, id: user ? user.id : null });
      trackPlaceAnOrder(cart, estimatedTotal);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  if (cart.length === 0) return (
    <div className="max-w-2xl mx-auto px-4 py-24 text-center">
      <h1 className="font-display font-extrabold text-3xl text-[#19324A]">Your bag is empty.</h1>
      <Link to="/shop" className="btn-primary mt-6 inline-flex">Go Sniffing for Goodies</Link>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#19324A] mb-8">Checkout</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-10">
        <form onSubmit={pay} className="space-y-6 min-w-0">
          <div className="bg-white rounded-3xl p-5 sm:p-6 soft-shadow">
            <h2 className="font-display font-bold text-xl text-[#19324A] mb-4">Contact</h2>
            <label className="block text-sm font-semibold text-[#19324A]/70 mb-1">Email Address</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
              className="w-full rounded-2xl border-2 border-[#19324A]/10 px-4 py-3 outline-none focus:border-[#FFD93D]" data-testid="checkout-email" />
            {!user && <p className="text-xs text-[#19324A]/50 mt-2">Checking out as a guest. <Link to="/login" className="text-[#4CC9F0] font-semibold">Log in</Link> to save your order history.</p>}
          </div>

          <div className="bg-white rounded-3xl p-5 sm:p-6 soft-shadow">
            <h2 className="font-display font-bold text-xl text-[#19324A] mb-2">Shipping & Payment</h2>
            <p className="text-[#19324A]/70 text-sm">Your shipping address and payment details are collected securely on the next step. We ship from Australia 🇦🇺 with international options available.</p>
            <div className="flex items-center gap-2 mt-4 text-sm text-[#19324A]/60"><ShieldCheck className="w-5 h-5 text-[#7ED957]" /> Secure payment powered by Stripe{testMode ? " (test mode)" : ""}</div>
            <CheckoutPostcode cart={cart} postcode={postcode} setPostcode={setPostcode} onEstimate={setEstimate} />
            {pickupPossible && (
              <label className="mt-3 flex items-start gap-3 rounded-2xl border-2 border-[#7ED957]/50 bg-[#7ED957]/10 p-4 cursor-pointer" data-testid="checkout-pickup-box">
                <input type="checkbox" checked={pickup} onChange={(e) => setPickup(e.target.checked)} className="mt-1 w-4 h-4 accent-[#166534]" data-testid="checkout-pickup" />
                <span className="text-sm text-[#19324A]"><strong>Local pickup — Slacks Creek QLD</strong> (no postage). We'll email you when it's ready to collect and arrange a time.{pickup && <span className="block text-xs font-semibold text-[#166534] mt-1" data-testid="checkout-pickup-note">✓ Flat postage waived at payment</span>}</span>
              </label>
            )}
          </div>

          <div className="bg-white rounded-3xl p-5 sm:p-6 soft-shadow">
            <h2 className="font-display font-bold text-xl text-[#19324A] mb-2">Promo code</h2>
            <p className="text-[#19324A]/70 text-sm mb-3">Got a promo or treat code? Pop it in.</p>
            <div className="flex gap-2">
              <input value={promoCode} onChange={(e) => setPromoCode(e.target.value)} placeholder="Your code"
                className="flex-1 min-w-0 rounded-2xl border-2 border-[#19324A]/10 px-4 py-3 outline-none focus:border-[#FF6B9D] uppercase" data-testid="promo-code-input" />
              <button type="button" onClick={applyPromo} disabled={checkingPromo} className="btn-outline !py-2.5" data-testid="promo-code-apply">{checkingPromo ? "Checking…" : "Apply"}</button>
            </div>
            {promo && <p className="text-sm text-[#7ED957] font-semibold mt-2" data-testid="promo-code-applied">Applied {promo.code} — {promo.free_shipping ? "free shipping 🎉" : `${formatAUD(promoDiscount)} off.`}</p>}
          </div>

          <div className="bg-white rounded-3xl p-5 sm:p-6 soft-shadow">
            <h2 className="font-display font-bold text-xl text-[#19324A] mb-2">Gift card</h2>
            <p className="text-[#19324A]/70 text-sm mb-3">Have a Happy Bulldog Co. gift card? Apply it here.</p>
            <div className="flex gap-2">
              <input value={giftCode} onChange={(e) => setGiftCode(e.target.value)} placeholder="HBC-XXXX-XXXX-XXXX"
                className="flex-1 min-w-0 rounded-2xl border-2 border-[#19324A]/10 px-4 py-3 outline-none focus:border-[#A78BFA] uppercase" data-testid="gift-card-input" />
              <button type="button" onClick={applyGift} disabled={checking} className="btn-outline !py-2.5" data-testid="gift-card-apply">{checking ? "Checking…" : "Apply"}</button>
            </div>
            {applied && <p className="text-sm text-[#7ED957] font-semibold mt-2" data-testid="gift-card-applied">Applied {applied.code} — {formatAUD(giftDiscount)} off this order.</p>}
          </div>

          <button type="submit" disabled={loading} className="btn-pink w-full !py-4 text-lg" data-testid="checkout-pay-btn">
            <Lock className="w-5 h-5" /> {loading ? "Redirecting…" : "Proceed to Secure Payment"} <ArrowRight className="w-5 h-5" />
          </button>
          <p className="text-xs text-center text-[#19324A]/45" data-testid="checkout-footnote">Prices in AUD.{testMode ? " Test card: 4242 4242 4242 4242, any future date & CVC." : " Secure card, Apple Pay and Google Pay via Stripe."}</p>
        </form>

        <div className="lg:sticky lg:top-24 self-start bg-white rounded-3xl p-5 sm:p-6 soft-shadow h-fit min-w-0">
          <h2 className="font-display font-bold text-xl text-[#19324A] mb-4">Order Summary</h2>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {cart.map((i) => (
              <div key={i.key} className="flex gap-3 items-center">
                <div className="relative">
                  <img src={i.image} alt={i.name} className="w-16 h-16 rounded-xl object-cover bg-[#FFF9EE]" />
                  <span className="absolute -top-2 -right-2 bg-[#19324A] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">{i.quantity}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-[#19324A] truncate">{i.name}</p>
                  {(i.size || i.colour || i.personalisation) && <p className="text-xs text-[#19324A]/50">{[i.colour, i.size].filter(Boolean).join(" · ")}{i.personalisation && <span className="text-[#FF6B9D] font-semibold">{(i.colour || i.size) ? " · " : ""}“{i.personalisation}”</span>}</p>}
                </div>
                <span className="font-semibold text-sm">{formatAUD(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-[#19324A]/10 mt-4 pt-4 flex justify-between font-display font-bold text-lg text-[#19324A]">
            <span>Subtotal</span><span>{formatAUD(subtotal)}</span>
          </div>
          {promoDiscount > 0 && (
            <div className="mt-2 flex justify-between text-sm font-semibold text-[#7ED957]">
              <span>Promo ({promo.code})</span><span>−{formatAUD(promoDiscount)}</span>
            </div>
          )}
          {giftDiscount > 0 && (
            <div className="mt-2 flex justify-between text-sm font-semibold text-[#7ED957]">
              <span>Gift card</span><span>−{formatAUD(giftDiscount)}</span>
            </div>
          )}
          {(promoDiscount > 0 || giftDiscount > 0) && (
            <div className="mt-2 flex justify-between font-display font-bold text-[#19324A]">
              <span>Estimated total</span><span data-testid="checkout-estimated-total">{formatAUD(estimatedTotal)}</span>
            </div>
          )}
          <p className="text-xs text-[#19324A]/50 mt-2">Shipping & taxes calculated at payment step.</p>
          {estimate && <p className="text-xs font-semibold text-[#19324A]/70 mt-1 inline-flex items-center gap-1" data-testid="summary-arrival">📦 Arrives {windowLabel(estimate)} to {estimate.postcode}</p>}
        </div>
      </div>
    </div>
  );
}
